import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppShell } from '../components/AppShell';
import { Button } from '../components/ui/button';
import { SendDocumentEmailDialog } from '../components/documents/SendDocumentEmailDialog';
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog';
import { ArrowLeft, BookOpen } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { parseSections, sectionsToText } from '../components/DocumentShared';
import { downloadBlob } from '../utils/downloadBlob';
import { safeBlobGet, safeBlobPost } from '../utils/fetchPdfBlob';
import { AutoGenerateCards } from '../components/documents/AutoGenerateCards';
import { InspectionEntrepriseForm } from '../components/documents/InspectionEntrepriseForm';
import { DocumentCardList } from '../components/documents/DocumentCardList';
import { DocumentWizard } from '../components/documents/DocumentWizard';
import { DocumentViewSheet } from '../components/documents/DocumentViewSheet';
import { VersionHistorySheet } from '../components/documents/VersionHistorySheet';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DOC_TYPE_VALUES = ['pgc', 'ppsps', 'diuo', 'icp'];

export function DocumentsCSPSPage() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterChantier, setFilterChantier] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardType, setWizardType] = useState('pgc');
  const [wizardChantier, setWizardChantier] = useState('');
  const [wizardEntreprise, setWizardEntreprise] = useState('');
  const [wizardNom, setWizardNom] = useState('');
  const [wizardVersion, setWizardVersion] = useState('1.0');
  const [wizardSummary, setWizardSummary] = useState(null);
  const [wizardSections, setWizardSections] = useState([]);

  // View/Edit state
  const [viewDoc, setViewDoc] = useState(null);
  const [emailingDoc, setEmailingDoc] = useState(null);
  const [viewSections, setViewSections] = useState([]);
  const [viewDirty, setViewDirty] = useState(false);
  const [versionHistory, setVersionHistory] = useState({ current: null, history: [] });
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);

  const [generating, setGenerating] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Auto-generation state
  const [autoSelections, setAutoSelections] = useState({ pgc: '', ppsps: '', diuo: '' });
  const [autoGenerating, setAutoGenerating] = useState({ pgc: false, ppsps: false, diuo: false });

  // Inspection state
  const [inspChantier, setInspChantier] = useState('');
  const [inspEntreprise, setInspEntreprise] = useState('');
  const [inspRepresentant, setInspRepresentant] = useState('');
  const [inspZone, setInspZone] = useState('');
  const [inspTravaux, setInspTravaux] = useState('');
  const [inspRisques, setInspRisques] = useState('');
  const [inspNC, setInspNC] = useState('');
  const [inspMesures, setInspMesures] = useState('');
  const [inspObs, setInspObs] = useState('');
  const [inspGenerating, setInspGenerating] = useState(false);
  const [inspHistory, setInspHistory] = useState([]);
  const [inspHistoryOpen, setInspHistoryOpen] = useState(false);
  const [inspEntreprises, setInspEntreprises] = useState([]);

  const [downloadingReferentiel, setDownloadingReferentiel] = useState(false);
  const handleDownloadReferentiel = async () => {
    setDownloadingReferentiel(true);
    try {
      const blob = await safeBlobGet(`${API}/referentiel-csps/pdf`);
      downloadBlob(blob, 'CHANTIX_Referentiel_CSPS.pdf');
      toast.success('Référentiel CSPS téléchargé');
    } catch (e) {
      toast.error('Erreur lors du téléchargement du référentiel');
    } finally {
      setDownloadingReferentiel(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [docsRes, chRes] = await Promise.all([
        axios.get(`${API}/csps/documents`, { withCredentials: true }),
        axios.get(`${API}/chantiers?type=csps`, { withCredentials: true }),
      ]);
      setDocuments((docsRes.data || []).filter(d => DOC_TYPE_VALUES.includes(d.type_document)));
      setChantiers((chRes.data || []).filter(c => c && c.id && c.name));
    } catch (_err) { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, [setDocuments, setChantiers, setLoading]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getChantierName = (id) => (chantiers || []).find(c => c && c.id === id)?.name || 'Chantier inconnu';

  const filtered = documents.filter(d => {
    const s = searchTerm.toLowerCase();
    return (!s || (d.nom || '').toLowerCase().includes(s) || (d.entreprise || '').toLowerCase().includes(s))
      && (filterType === 'all' || d.type_document === filterType)
      && (filterChantier === 'all' || d.chantier_id === filterChantier)
      && (filterStatus === 'all' || d.status === filterStatus);
  });

  // --- Wizard handlers ---
  const openWizard = () => {
    setWizardStep(1); setWizardType('pgc');
    setWizardChantier(chantiers[0]?.id || '');
    setWizardEntreprise(''); setWizardNom(''); setWizardVersion('1.0');
    setWizardSummary(null); setWizardSections([]);
    setWizardOpen(true);
  };

  const generateTemplate = async () => {
    if (!wizardChantier) { toast.error('Sélectionnez un chantier'); return; }
    if (wizardType === 'icp' && !wizardEntreprise.trim()) { toast.error("Saisissez le nom de l'entreprise pour l'ICP"); return; }
    setGenerating(true);
    try {
      const payload = { chantier_id: wizardChantier, type_document: wizardType };
      if (wizardType === 'icp') payload.entreprise = wizardEntreprise.trim();
      const res = await axios.post(`${API}/csps/documents/template`, payload, { withCredentials: true });
      setWizardNom(res.data.nom); setWizardSummary(res.data.summary || null);
      setWizardSections(parseSections(res.data.contenu)); setWizardStep(2);
      toast.success('Template généré');
    } catch (_err) { toast.error('Erreur generation'); }
    finally { setGenerating(false); }
  };

  const enrichAll = async () => {
    setEnriching(true);
    try {
      const contenu = sectionsToText(wizardSections);
      const res = await axios.post(`${API}/csps/documents/enrich`, {
        chantier_id: wizardChantier, type_document: wizardType, contenu,
      }, { withCredentials: true });
      setWizardSections(parseSections(res.data.contenu));
      toast.success('Document complete');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setEnriching(false); }
  };

  const saveWizardDoc = async () => {
    setSubmitting(true);
    try {
      const contenu = sectionsToText(wizardSections);
      const payload = {
        chantier_id: wizardChantier, nom: wizardNom, type_document: wizardType,
        version: wizardVersion, status: 'brouillon', contenu,
      };
      if (wizardType === 'icp' && wizardEntreprise && wizardEntreprise !== '__autre__') {
        payload.entreprise = wizardEntreprise;
      }
      await axios.post(`${API}/csps/documents`, payload, { withCredentials: true });
      setWizardOpen(false); toast.success('Document cree'); fetchData();
    } catch (_err) { toast.error('Erreur creation'); }
    finally { setSubmitting(false); }
  };

  // --- View/Edit handlers ---
  const openView = (doc) => {
    setViewDoc(doc); setViewSections(parseSections(doc.contenu || '')); setViewDirty(false);
  };

  const saveViewDoc = async () => {
    if (!viewDoc) return;
    setSubmitting(true);
    try {
      const contenu = sectionsToText(viewSections);
      await axios.put(`${API}/csps/documents/${viewDoc.id}`, { contenu }, { withCredentials: true });
      toast.success('Document mis à jour'); setViewDoc(null); fetchData();
    } catch (_err) { toast.error('Erreur'); }
    finally { setSubmitting(false); }
  };

  const enrichViewDoc = async () => {
    if (!viewDoc) return;
    setEnriching(true);
    try {
      const contenu = sectionsToText(viewSections);
      const res = await axios.post(`${API}/csps/documents/enrich`, {
        chantier_id: viewDoc.chantier_id, type_document: viewDoc.type_document, contenu,
      }, { withCredentials: true });
      setViewSections(parseSections(res.data.contenu)); setViewDirty(true);
      toast.success('Document complete');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setEnriching(false); }
  };

  // --- Actions ---
  const handleValidate = async (doc) => {
    try {
      const res = await axios.post(`${API}/csps/documents/${doc.id}/validate`, {}, { withCredentials: true });
      toast.success(`Document valide et verrouille (V${res.data.version})`); fetchData();
      if (viewDoc?.id === doc.id) {
        setViewDoc(res.data); setViewSections(parseSections(res.data.contenu || '')); setViewDirty(false);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur validation'); }
  };

  const handleNewVersion = async (doc) => {
    try {
      const res = await axios.post(`${API}/csps/documents/${doc.id}/new-version`, {}, { withCredentials: true });
      toast.success(`Nouvelle version V${res.data.version} créée`); fetchData();
      if (viewDoc?.id === doc.id) {
        setViewDoc(res.data); setViewSections(parseSections(res.data.contenu || '')); setViewDirty(false);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const downloadPdf = async (doc) => {
    try {
      const res = await safeBlobGet(`${API}/csps/documents/${doc.id}/pdf`);
      downloadBlob(res, `${doc.type_document.toUpperCase()}_${doc.nom.replace(/\s/g, '_')}_v${doc.version}.pdf`);
      toast.success('PDF telecharge');
    } catch (_err) { toast.error(_err.message || "Validez le document avant l'export"); }
  };

  const downloadDocx = async (doc) => {
    try {
      const res = await safeBlobGet(`${API}/csps/documents/${doc.id}/docx`);
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const filename = `${doc.type_document.toUpperCase()}_${doc.nom.replace(/\s/g, '_')}_v${doc.version}.docx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename; a.style.display = 'none';
      document.body.appendChild(a); a.click();
      setTimeout(() => { window.URL.revokeObjectURL(url); }, 10000);
      try { document.body.removeChild(a); } catch (_e) { void _e; }
      toast.success('Word telecharge');
    } catch (_err) { toast.error(_err.message || "Validez le document avant l'export"); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/csps/documents/${id}`, { withCredentials: true });
      setDeleteConfirm(null);
      if (viewDoc?.id === id) setViewDoc(null);
      toast.success('Supprimé'); fetchData();
    } catch (_err) { toast.error('Erreur suppression'); }
  };

  const fetchVersionHistory = async (docId) => {
    try {
      const res = await axios.get(`${API}/csps/documents/${docId}/versions`, { withCredentials: true });
      return res.data;
    } catch (_err) { return { current: null, history: [] }; }
  };

  // Auto-generate handlers
  const handleAutoGenerate = async (type, chantierId) => {
    if (!chantierId) { toast.error('Sélectionnez un chantier CSPS'); return; }
    setAutoGenerating(prev => ({ ...prev, [type]: true }));
    try {
      const res = await safeBlobGet(`${API}/csps/documents/${type}/auto?chantier_id=${chantierId}`);
      downloadBlob(res, `${type.toUpperCase()}.pdf`);
      toast.success(`${type.toUpperCase()} généré avec succes`);
    } catch (_err) { toast.error(_err.message || `Erreur lors de la generation du ${type.toUpperCase()}`); }
    finally { setAutoGenerating(prev => ({ ...prev, [type]: false })); }
  };

  // Inspection handlers
  const fetchInspectionHistory = useCallback(async (chantierId) => {
    if (!chantierId) { setInspHistory([]); return; }
    try {
      const res = await axios.get(`${API}/csps/documents/inspections-entreprise?chantier_id=${chantierId}`, { withCredentials: true });
      setInspHistory(res.data || []);
    } catch (_err) { setInspHistory([]); }
  }, [setInspHistory]);

  const handleGenerateInspection = async () => {
    if (!inspChantier) { toast.error('Sélectionnez un chantier'); return; }
    if (!inspEntreprise.trim()) { toast.error("Renseignez le nom de l'entreprise"); return; }
    if (!inspRepresentant.trim()) { toast.error('Renseignez le representant'); return; }
    setInspGenerating(true);
    try {
      const res = await safeBlobPost(`${API}/csps/documents/inspection-entreprise/generate`, {
        chantier_id: inspChantier, entreprise: inspEntreprise, representant: inspRepresentant,
        zone_intervention: inspZone, travaux_concernes: inspTravaux,
        risques_identifies: inspRisques, non_conformites: inspNC,
        mesures_correctives: inspMesures, observations: inspObs,
      });
      downloadBlob(res, 'Inspection_Entreprise.pdf');
      toast.success('Inspection générée avec succes');
      setInspEntreprise(''); setInspRepresentant(''); setInspZone('');
      setInspTravaux(''); setInspRisques(''); setInspNC('');
      setInspMesures(''); setInspObs('');
      fetchInspectionHistory(inspChantier);
    } catch (_err) { toast.error("Erreur lors de la generation de l'inspection"); }
    finally { setInspGenerating(false); }
  };

  useEffect(() => {
    if (inspChantier) {
      fetchInspectionHistory(inspChantier);
      (async () => {
        try {
          const res = await axios.get(`${API}/chantiers/${inspChantier}/entreprises`, { withCredentials: true });
          setInspEntreprises(res.data || []);
        } catch (_err) { setInspEntreprises([]); }
      })();
    }
  }, [inspChantier, fetchInspectionHistory]);

  if (loading) return <AppShell><div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" /></div></AppShell>;

  return (
    <AppShell>
      <div className="space-y-4 sm:space-y-6" data-testid="csps-documents-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" className="h-10 w-10 md:hidden flex-shrink-0"
              onClick={() => navigate('/csps/chantiers')} data-testid="back-btn">
              <ArrowLeft size={22} />
            </Button>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#111827] truncate" data-testid="csps-documents-title">Documents CSPS</h1>
              <p className="text-sm text-[#4B5563]">Génération automatique et gestion documentaire</p>
            </div>
          </div>
          <Button
            onClick={handleDownloadReferentiel}
            disabled={downloadingReferentiel}
            className="bg-gradient-to-r from-[#FF6B00] to-[#FF8C42] text-white hover:opacity-90 shadow-sm flex-shrink-0"
            data-testid="download-referentiel-csps-btn"
          >
            {downloadingReferentiel ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <BookOpen size={18} weight="bold" />
            )}
            <span className="ml-2 hidden sm:inline">Référentiel CSPS</span>
            <span className="ml-2 sm:hidden">Guide</span>
          </Button>
        </div>

        <AutoGenerateCards
          chantiers={chantiers}
          selections={autoSelections}
          onSelectionChange={(type, value) => setAutoSelections(prev => ({ ...prev, [type]: value }))}
          onGenerate={handleAutoGenerate}
          generatingStates={autoGenerating}
        />

        <InspectionEntrepriseForm
          chantiers={chantiers}
          inspChantier={inspChantier} setInspChantier={setInspChantier}
          inspEntreprise={inspEntreprise} setInspEntreprise={setInspEntreprise}
          inspRepresentant={inspRepresentant} setInspRepresentant={setInspRepresentant}
          inspZone={inspZone} setInspZone={setInspZone}
          inspTravaux={inspTravaux} setInspTravaux={setInspTravaux}
          inspRisques={inspRisques} setInspRisques={setInspRisques}
          inspNC={inspNC} setInspNC={setInspNC}
          inspMesures={inspMesures} setInspMesures={setInspMesures}
          inspObs={inspObs} setInspObs={setInspObs}
          inspGenerating={inspGenerating} inspEntreprises={inspEntreprises}
          inspHistory={inspHistory}
          inspHistoryOpen={inspHistoryOpen} setInspHistoryOpen={setInspHistoryOpen}
          onGenerate={handleGenerateInspection}
        />

        <DocumentCardList
          documents={filtered} chantiers={chantiers} getChantierName={getChantierName}
          searchTerm={searchTerm} setSearchTerm={setSearchTerm}
          filterType={filterType} setFilterType={setFilterType}
          filterChantier={filterChantier} setFilterChantier={setFilterChantier}
          filterStatus={filterStatus} setFilterStatus={setFilterStatus}
          onOpenWizard={openWizard} onOpenView={openView}
          onValidate={handleValidate} onNewVersion={handleNewVersion}
          onDownloadPdf={downloadPdf} onDownloadDocx={downloadDocx}
          onSendEmail={setEmailingDoc}
          onDeleteConfirm={setDeleteConfirm}
        />
      </div>

      <DocumentWizard
        open={wizardOpen} onOpenChange={setWizardOpen} chantiers={chantiers}
        wizardStep={wizardStep} setWizardStep={setWizardStep}
        wizardType={wizardType} setWizardType={setWizardType}
        wizardChantier={wizardChantier} setWizardChantier={setWizardChantier}
        wizardEntreprise={wizardEntreprise} setWizardEntreprise={setWizardEntreprise}
        wizardNom={wizardNom} setWizardNom={setWizardNom}
        wizardVersion={wizardVersion} setWizardVersion={setWizardVersion}
        wizardSummary={wizardSummary} wizardSections={wizardSections} setWizardSections={setWizardSections}
        generating={generating} enriching={enriching} submitting={submitting}
        onGenerateTemplate={generateTemplate} onEnrichAll={enrichAll} onSave={saveWizardDoc}
      />

      <DocumentViewSheet
        viewDoc={viewDoc} onClose={() => setViewDoc(null)} chantiers={chantiers} getChantierName={getChantierName}
        viewSections={viewSections}
        setSectionsWithDirty={(s) => { setViewSections(s); setViewDirty(true); }}
        viewDirty={viewDirty} enriching={enriching} submitting={submitting}
        onEnrich={enrichViewDoc} onValidate={handleValidate} onNewVersion={handleNewVersion}
        onDownloadPdf={downloadPdf} onDownloadDocx={downloadDocx} onSave={saveViewDoc}
        onCancel={() => { setViewSections(parseSections(viewDoc.contenu || '')); setViewDirty(false); }}
        onShowVersionHistory={async () => {
          const data = await fetchVersionHistory(viewDoc.id);
          setVersionHistory(data); setVersionHistoryOpen(true);
        }}
      />

      <VersionHistorySheet open={versionHistoryOpen} onOpenChange={setVersionHistoryOpen} versionHistory={versionHistory} />

      <DeleteConfirmDialog
        item={deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDelete}
        titlePrefix="Supprimér ce document"
        entityLabel={deleteConfirm?.nom}
      />

      <SendDocumentEmailDialog
        open={!!emailingDoc}
        onClose={() => setEmailingDoc(null)}
        docType="csps_doc"
        pdfUrl={emailingDoc ? `${API}/csps/documents/${emailingDoc.id}/pdf` : null}
        filename={emailingDoc ? `${(emailingDoc.type_document || 'doc').toUpperCase()}_${(emailingDoc.nom || '').replace(/\s/g,'_')}_v${emailingDoc.version || 1}.pdf` : ''}
        subject={emailingDoc ? `[CHANTIX] ${(emailingDoc.type_document || 'Document').toUpperCase()} — ${emailingDoc.nom || ''}` : ''}
        docTitle={emailingDoc ? `${(emailingDoc.type_document || '').toUpperCase()} · ${emailingDoc.nom || ''} (v${emailingDoc.version || 1})` : ''}
        suggestedRecipients={[]}
      />
    </AppShell>
  );
}
